import { useState, useSyncExternalStore } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { EventListHeader } from '@/components/events/engagement-event-controls'
import { EngagementEventTable } from '@/components/events/engagement-event-table'
import { EngagementEventPeopleSheet } from '@/components/events/engagement-event-people-sheet'
import { getJSON } from '@/lib/api'
import type {
  EngagementEvent,
  EngagementEventRegistrationsResponse,
} from '@/lib/engagement-event-registrations'
import { pushLocation, subscribeLocation } from './timelineFilters'

const fields = ['q', 'when', 'year', 'sort', 'cursor']
export function OrganizationEventRegistrationsList({ contactId }: { contactId: number }) {
  const search = useSyncExternalStore(subscribeLocation, () => window.location.search)
  const params = new URLSearchParams(search)
  const [opened, setOpened] = useState<{
    row: EngagementEvent
    trigger: HTMLElement
    contactId: number
  } | null>(null)
  const queryParams = new URLSearchParams()
  for (const field of fields) if (params.has(field)) queryParams.set(field, params.get(field)!)
  const base = `/api/v1/contacts/${contactId}/engagement/event_registrations`
  const path = `${base}?${queryParams}`
  const query = useQuery({
    queryKey: ['engagement-events', path],
    queryFn: () => getJSON<EngagementEventRegistrationsResponse>(path),
    retry: false,
  })
  const update = (key: string, value: string) => {
    const url = new URL(window.location.href)
    url.searchParams.delete('cursor')
    if (value) url.searchParams.set(key, value)
    else url.searchParams.delete(key)
    pushLocation(url)
  }
  const data = query.data?.event_registrations
  queryParams.delete('cursor')
  return (
    <>
      <Card>
        <EventListHeader
          data={data}
          params={params}
          update={update}
          csvHref={`${base}.csv?${queryParams}`}
        />
        <EventListBody
          query={query}
          sort={params.get('sort') ?? 'date:desc'}
          onSort={(value) => update('sort', value)}
          onOpen={(row, trigger) => setOpened({ row, trigger, contactId })}
        />
        {data && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <span className="text-caption text-muted-foreground">{data.pagination.caption}</span>
            <div className="flex gap-2">
              {data.pagination.previous_cursor !== null && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => update('cursor', data.pagination.previous_cursor!)}
                >
                  Previous
                </Button>
              )}
              {data.pagination.next_cursor !== null && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => update('cursor', data.pagination.next_cursor!)}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>
      {opened?.contactId === contactId && (
        <EngagementEventPeopleSheet
          key={`${contactId}-${opened.row.event.id}`}
          contactId={contactId}
          row={opened.row}
          trigger={opened.trigger}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  )
}

function EventListBody({
  query,
  sort,
  onSort,
  onOpen,
}: {
  query: UseQueryResult<EngagementEventRegistrationsResponse>
  sort: string
  onSort: (value: string) => void
  onOpen: (row: EngagementEvent, trigger: HTMLElement) => void
}) {
  if (query.isFetching) return <EmptyState variant="quiet" title="Loading event registrations" />
  if (query.error)
    return (
      <Alert variant="destructive">Could not load event registrations. {query.error.message}</Alert>
    )
  const data = query.data?.event_registrations
  if (data?.empty_message) return <EmptyState variant="quiet" title={data.empty_message} />
  return (
    data && <EngagementEventTable rows={data.rows} sort={sort} onSort={onSort} onOpen={onOpen} />
  )
}
